<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useApolloClient } from '@vue/apollo-composable';
import { useEventListener, useResizeObserver } from '@vueuse/core';

import {
  ensureFreshBinaryFileUrls,
  holdBinaryFileUrls,
  peekBinaryFileUrls,
} from '../RichTextView/attachments/useBinaryFileHydration';
import { closeLightbox, lightboxState, stepLightbox } from './lightbox-store';

const MIN_SCALE = 1;
const MAX_SCALE = 8;
/** Where a double-click / zoom-in tap lands from the fitted view. */
const STEP_SCALE = 2.5;
const DRAG_SLOP_PX = 4;

/**
 * A double-click on the *thumbnail* delivers its second click to the overlay
 * that the first click just opened. Swallow dblclick-zoom for a beat so the
 * image does not jump straight to 2.5x on open.
 */
const OPEN_SETTLE_MS = 350;

const open = computed({
  get: () => lightboxState.open,
  set: (value: boolean) => {
    if (!value) {
      closeLightbox();
    }
  },
});

// Resolved on use, not here: only a stored file needs the client.
const apollo = useApolloClient();

const current = computed(() => lightboxState.items[lightboxState.index] ?? null);
const total = computed(() => lightboxState.items.length);
const hasGallery = computed(() => total.value > 1);

/**
 * A stored file's URLs are read live from the attachment cache, which renews
 * them: the snapshot taken when the viewer opened is a presigned URL that dies
 * within the hour, on a viewer left open or opened after the laptop slept.
 */
const stored = computed(() => {
  const entry = current.value?.fileId ? peekBinaryFileUrls(current.value.fileId) : undefined;

  return entry?.status === 'ready' ? entry : undefined;
});

const currentSrc = computed(() => stored.value?.url ?? current.value?.src ?? '');
const currentDownloadUrl = computed(() => stored.value?.downloadUrl ?? current.value?.downloadUrl ?? null);

const caption = computed(() => current.value?.fileName || current.value?.alt || '');
const downloadHref = computed(() => currentDownloadUrl.value ?? (currentSrc.value || null));

const stage = ref<HTMLElement | null>(null);
const imageEl = ref<HTMLImageElement | null>(null);

const scale = ref(MIN_SCALE);
const translateX = ref(0);
const translateY = ref(0);
const isZoomed = computed(() => scale.value > MIN_SCALE);

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/**
 * Keep the image anchored to the stage: never let a pan drag it past the point
 * where its own edge crosses the viewport edge.
 */
const clampPan = (): void => {
  const image = imageEl.value;
  const box = stage.value;

  if (!image || !box) {
    return;
  }

  // `offsetWidth` ignores the CSS transform, so it is the fitted (1x) size.
  const maxX = Math.max(0, (image.offsetWidth * scale.value - box.clientWidth) / 2);
  const maxY = Math.max(0, (image.offsetHeight * scale.value - box.clientHeight) / 2);

  translateX.value = clamp(translateX.value, -maxX, maxX);
  translateY.value = clamp(translateY.value, -maxY, maxY);
};

/**
 * Zooms to `next`, keeping the point under (originX, originY) pinned — the
 * behaviour that makes wheel-zoom feel like it targets what you point at
 * rather than the middle of the screen.
 */
const zoomTo = (next: number, originX?: number, originY?: number): void => {
  const target = clamp(next, MIN_SCALE, MAX_SCALE);
  const box = stage.value;

  if (box && originX !== undefined && originY !== undefined) {
    const rect = box.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Position of the cursor in the image's own (unscaled) coordinate space.
    const pointX = (originX - centerX - translateX.value) / scale.value;
    const pointY = (originY - centerY - translateY.value) / scale.value;

    translateX.value = originX - centerX - pointX * target;
    translateY.value = originY - centerY - pointY * target;
  }

  scale.value = target;

  // Snapping back to the fitted view always re-centres — a stranded offset at
  // 1x would show as an image that refuses to sit in the middle.
  if (target === MIN_SCALE) {
    translateX.value = 0;
    translateY.value = 0;
  }

  clampPan();
};

const resetView = (): void => {
  scale.value = MIN_SCALE;
  translateX.value = 0;
  translateY.value = 0;
};

const zoomIn = (): void => zoomTo(scale.value * 1.5);
const zoomOut = (): void => zoomTo(scale.value / 1.5);

const close = (): void => {
  open.value = false;
};

const goTo = (delta: number): void => {
  stepLightbox(delta);
};

// ---------------------------------------------------------------------------
// Pointer handling — one code path covers mouse drag, touch pan and pinch.
// ---------------------------------------------------------------------------

const pointers = new Map<number, { x: number; y: number }>();
let dragOrigin: { x: number; y: number; translateX: number; translateY: number } | null = null;
/**
 * `distance`/`scale` are the gesture baseline — fixed until the pointer pair
 * changes, so zoom stays absolute rather than compounding per frame. The
 * centroid is the opposite: it is the *last observed* midpoint, advanced every
 * move, because panning needs the delta since the previous frame.
 */
let pinchOrigin: { distance: number; scale: number; centroidX: number; centroidY: number } | null = null;

/** Drives the transition class: a live gesture must track the pointer 1:1. */
const isInteracting = ref(false);

/**
 * True once the pointer travelled past the slop. A drag that starts on the
 * image and ends over the backdrop still fires `click` on their common
 * ancestor (the stage), which would otherwise read as "clicked outside" and
 * close the lightbox mid-pan.
 */
const didDrag = ref(false);

const pointerPair = (): [{ x: number; y: number }, { x: number; y: number }] | null => {
  const [first, second] = [...pointers.values()];

  return first && second ? [first, second] : null;
};

/** Distance between the two pointers currently driving a pinch. */
const pairDistance = (pair: [{ x: number; y: number }, { x: number; y: number }]): number =>
  Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);

/** Midpoint of the two pointers — the point a pinch is felt to happen around. */
const pairCentroid = (pair: [{ x: number; y: number }, { x: number; y: number }]): { x: number; y: number } => ({
  x: (pair[0].x + pair[1].x) / 2,
  y: (pair[0].y + pair[1].y) / 2,
});

/** Re-measure the pinch baseline — the pair changed, so the old one is void. */
const seedPinch = (): void => {
  const pair = pointerPair();

  if (!pair) {
    pinchOrigin = null;

    return;
  }

  const centroid = pairCentroid(pair);

  pinchOrigin = {
    distance: pairDistance(pair),
    scale: scale.value,
    centroidX: centroid.x,
    centroidY: centroid.y,
  };
};

const seedDrag = (x: number, y: number): void => {
  dragOrigin = { x, y, translateX: translateX.value, translateY: translateY.value };
};

/**
 * The chrome (close, arrows, zoom pill) must keep native button semantics —
 * capturing the pointer there would make a press-and-drag-away still fire the
 * click, and would latch the drag guard on an ordinary tap.
 */
const isChrome = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest('button, a'));

const onPointerDown = (event: PointerEvent): void => {
  // Ignore right/middle mouse buttons; touch and pen report button 0.
  if (event.pointerType === 'mouse' && event.button !== 0) {
    return;
  }

  if (isChrome(event.target)) {
    return;
  }

  // Capture on the pressed element, NOT the stage: Chrome retargets the
  // follow-up `click` to the capture element, so capturing on the stage would
  // make every click — including one on the image — satisfy `@click.self` and
  // close the viewer.
  (event.target as Element).setPointerCapture?.(event.pointerId);
  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  didDrag.value = false;
  isInteracting.value = true;

  if (pointers.size >= 2) {
    seedPinch();
    dragOrigin = null;

    return;
  }

  seedDrag(event.clientX, event.clientY);
};

const onPointerMove = (event: PointerEvent): void => {
  if (!pointers.has(event.pointerId)) {
    return;
  }

  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

  const pair = pointerPair();

  if (pair && pinchOrigin) {
    didDrag.value = true;

    const centroid = pairCentroid(pair);

    // Anchor the zoom on the PREVIOUS centroid, then translate by however far
    // the centroid itself travelled. That order is what pins the image point
    // the fingers grabbed under where those fingers now are; anchoring on the
    // new centroid instead leaves a residual that compounds over a gesture.
    zoomTo(
      pinchOrigin.scale * (pairDistance(pair) / pinchOrigin.distance),
      pinchOrigin.centroidX,
      pinchOrigin.centroidY,
    );

    // Two fingers moving together hold their distance, which makes the zoom
    // above an identity — without this step the image ignores a two-finger
    // drag entirely and only ever responds to the spread between the fingers.
    translateX.value += centroid.x - pinchOrigin.centroidX;
    translateY.value += centroid.y - pinchOrigin.centroidY;
    clampPan();

    pinchOrigin.centroidX = centroid.x;
    pinchOrigin.centroidY = centroid.y;

    return;
  }

  if (!dragOrigin) {
    return;
  }

  const deltaX = event.clientX - dragOrigin.x;
  const deltaY = event.clientY - dragOrigin.y;

  // Only a zoomed image can move; at 1x the gesture is an ordinary click, and
  // latching the guard there would break tap-to-dismiss on touch, where a tap
  // routinely travels a few pixels.
  if (!isZoomed.value) {
    return;
  }

  if (Math.hypot(deltaX, deltaY) > DRAG_SLOP_PX) {
    didDrag.value = true;
  }

  translateX.value = dragOrigin.translateX + deltaX;
  translateY.value = dragOrigin.translateY + deltaY;
  clampPan();
};

const onPointerUp = (event: PointerEvent): void => {
  // A mouse reports one pointerId for every button, so releasing a secondary
  // button must not tear down a drag the primary button is still driving.
  if (event.pointerType === 'mouse' && event.button !== 0) {
    return;
  }

  if (!pointers.delete(event.pointerId)) {
    return;
  }

  if (pointers.size === 0) {
    pinchOrigin = null;
    dragOrigin = null;
    isInteracting.value = false;

    // `didDrag` is intentionally left set — the `click` that follows this
    // `pointerup` reads it, and the next `pointerdown` clears it.
    return;
  }

  if (pointers.size === 1) {
    // Down to one finger: hand the gesture back to pan, anchored where that
    // finger actually is, or it stays frozen until the user lifts and re-taps.
    const [remaining] = [...pointers.values()];

    pinchOrigin = null;

    if (remaining) {
      seedDrag(remaining.x, remaining.y);
    }

    return;
  }

  // Still pinching, but with a different pair — the old baseline is meaningless.
  seedPinch();
};

const onWheel = (event: WheelEvent): void => {
  // A trackpad pinch arrives as a wheel event with `ctrlKey` and much larger
  // deltas than a mouse notch — scale the sensitivity accordingly.
  const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0025));

  zoomTo(scale.value * factor, event.clientX, event.clientY);
};

let openedAt = 0;

const onImageDoubleClick = (event: MouseEvent): void => {
  if (Date.now() - openedAt < OPEN_SETTLE_MS) {
    return;
  }

  if (isZoomed.value) {
    zoomTo(MIN_SCALE);

    return;
  }

  zoomTo(STEP_SCALE, event.clientX, event.clientY);
};

const onStageClick = (): void => {
  // Same settle window as the double-click guard, for the mirror-image case: a
  // habitual double-click on a read-only thumbnail opens the viewer with the
  // first click and would land the second one on the backdrop, flashing it shut.
  if (didDrag.value || Date.now() - openedAt < OPEN_SETTLE_MS) {
    return;
  }

  close();
};

const onDownload = async (): Promise<void> => {
  // The image clicked, held across the renewal: the viewer may have moved on
  // to another one by the time it answers.
  const item = current.value;

  if (!item) {
    return;
  }

  const renewed = item.fileId ? await ensureFreshBinaryFileUrls(apollo.client, item.fileId) : undefined;
  const stored = renewed?.status === 'ready' ? renewed : undefined;
  const downloadUrl = stored?.downloadUrl ?? item.downloadUrl ?? null;
  const href = downloadUrl ?? (stored?.url || item.src || null);

  if (!href) {
    return;
  }

  // `download` is honoured only same-origin; for anything else the browser
  // ignores it and a bare anchor click navigates the SPA away, losing whatever
  // the user had open. Only the managed `downloadUrl` — a presigned GET with
  // `Content-Disposition: attachment` — is safe to click in place.
  if (!downloadUrl) {
    window.open(href, '_blank', 'noopener,noreferrer');

    return;
  }

  const link = document.createElement('a');

  link.href = href;
  link.rel = 'noopener';

  if (item.fileName) {
    link.download = item.fileName;
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ---------------------------------------------------------------------------
// Keyboard — active only while open, so page shortcuts keep working otherwise.
// ---------------------------------------------------------------------------

const PAN_STEP_PX = 60;

useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (!lightboxState.open) {
    return;
  }

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      // While zoomed the arrows nudge the viewport instead of changing image —
      // otherwise inspecting a wide screenshot would keep skipping away.
      if (isZoomed.value) {
        translateX.value += PAN_STEP_PX;
        clampPan();
      } else {
        goTo(-1);
      }
      break;
    case 'ArrowRight':
      event.preventDefault();
      if (isZoomed.value) {
        translateX.value -= PAN_STEP_PX;
        clampPan();
      } else {
        goTo(1);
      }
      break;
    case 'ArrowUp':
      if (isZoomed.value) {
        event.preventDefault();
        translateY.value += PAN_STEP_PX;
        clampPan();
      }
      break;
    case 'ArrowDown':
      if (isZoomed.value) {
        event.preventDefault();
        translateY.value -= PAN_STEP_PX;
        clampPan();
      }
      break;
    case '+':
    case '=':
      event.preventDefault();
      zoomIn();
      break;
    case '-':
    case '_':
      event.preventDefault();
      zoomOut();
      break;
    case '0':
      event.preventDefault();
      resetView();
      break;
    default:
      break;
  }
});

/**
 * Reka autofocuses the first focusable element, which lands a focus ring on
 * the close button the moment the viewer opens. Move focus to the stage
 * instead — it keeps the dialog's focus trap honest without the artifact.
 */
const onOpenAutoFocus = (event: Event): void => {
  event.preventDefault();
  stage.value?.focus({ preventScroll: true });
};

/** Element that held focus when the viewer opened, so close can hand it back. */
let focusOrigin: HTMLElement | null = null;

/**
 * Reka restores focus to whatever was focused before opening. Its own `focus()`
 * has no `preventScroll`, and the origin is usually the ProseMirror
 * contenteditable holding the image — focusing that re-asserts the caret and
 * the browser scrolls it into view, which reads as the page jumping a few
 * pixels the instant the lightbox closes. Restore the same element ourselves,
 * without the scroll.
 */
const onCloseAutoFocus = (event: Event): void => {
  event.preventDefault();
  focusOrigin?.focus?.({ preventScroll: true });
  focusOrigin = null;
};

/**
 * The image is sized against the stage, so a resize (window, rotation, sidebar)
 * changes the pan bounds — a retained offset would leave a gap at one edge.
 */
useResizeObserver(stage, () => clampPan());

const loadFailed = ref(false);

// A new image — or a fresh open — always starts fitted.
watch(() => lightboxState.index, () => {
  resetView();
  loadFailed.value = false;
});

// A stored image is shown with URLs renewed first if they are near expiry, and
// kept renewed while it stays open; one that failed on an expired URL gets
// another go with the renewed one.
watch(() => (lightboxState.open ? current.value?.fileId : null), (fileId, _previous, onCleanup) => {
  if (fileId) {
    onCleanup(holdBinaryFileUrls(fileId));
    void ensureFreshBinaryFileUrls(apollo.client, fileId);
  }
}, { immediate: true });

watch(currentSrc, () => {
  loadFailed.value = false;
});

watch(
  () => lightboxState.open,
  (value) => {
    if (value) {
      openedAt = Date.now();
      resetView();
      loadFailed.value = false;
      // Runs before the dialog mounts, so this is still the pre-open focus —
      // the element Reka would otherwise scroll back into view on close.
      focusOrigin = document.activeElement as HTMLElement | null;
    }

    pointers.clear();
    dragOrigin = null;
    pinchOrigin = null;
    didDrag.value = false;
    isInteracting.value = false;
  },
);
</script>

<template>
  <UModal
    v-model:open="open"
    title="Image preview"
    :content="{ onOpenAutoFocus, onCloseAutoFocus }"
    :ui="{
      content: 'max-w-none w-screen h-screen bg-transparent shadow-none ring-0 rounded-none sm:rounded-none',
      overlay: 'bg-black/95 backdrop-blur-sm',
    }"
    data-testid="image-lightbox"
  >
    <template #content>
      <div
        ref="stage"
        tabindex="-1"
        class="relative flex h-screen w-screen touch-none items-center justify-center overflow-hidden p-4 select-none focus:outline-none sm:p-10"
        data-testid="image-lightbox-stage"
        @click.self="onStageClick"
        @wheel.prevent="onWheel"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <!-- A presigned URL is snapshotted when the viewer opens and can expire
             while it stays open; say so rather than showing a broken frame. -->
        <div
          v-if="loadFailed"
          class="flex flex-col items-center gap-3 text-white/80"
          data-testid="image-lightbox-error"
        >
          <UIcon
            name="i-lucide-image-off"
            class="size-8"
          />
          <p class="text-sm">
            This image could not be loaded. Close the preview and try again.
          </p>
        </div>

        <img
          v-else-if="current"
          ref="imageEl"
          :src="currentSrc"
          :alt="current.alt || 'Image preview'"
          class="max-h-full max-w-full rounded-md object-contain"
          :class="[
            isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
            // The transform is animated only for discrete jumps (buttons, keys,
            // double-click); a live drag must track the pointer with no lag.
            isInteracting ? '' : 'transition-transform duration-150 ease-out',
          ]"
          :style="{
            transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
          }"
          draggable="false"
          data-testid="image-lightbox-image"
          @dblclick="onImageDoubleClick"
          @error="loadFailed = true"
        >

        <!-- Top bar: gallery position on the left, close on the right. The
             gradient itself is click-through so the backdrop still closes. -->
        <div
          class="pointer-events-none fixed inset-x-0 top-0 z-10 flex items-start justify-between gap-4 bg-gradient-to-b from-black/70 to-transparent px-[max(1rem,env(safe-area-inset-left))] pt-[max(0.75rem,env(safe-area-inset-top))] pb-12"
        >
          <span
            v-if="hasGallery"
            class="pointer-events-auto rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white tabular-nums"
            data-testid="image-lightbox-counter"
          >
            {{ lightboxState.index + 1 }} / {{ total }}
          </span>
          <span v-else />

          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="lg"
            class="pointer-events-auto shrink-0 text-white hover:bg-white/15"
            aria-label="Close"
            data-testid="image-lightbox-close"
            @click.stop="close"
          />
        </div>

        <!-- Gallery arrows: always reachable, never over the
             image's centre. -->
        <template v-if="hasGallery">
          <UButton
            icon="i-lucide-chevron-left"
            color="neutral"
            variant="ghost"
            size="xl"
            class="absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full bg-black/40 text-white hover:bg-white/20 sm:left-4"
            aria-label="Previous image"
            data-testid="image-lightbox-prev"
            @click.stop="goTo(-1)"
          />
          <UButton
            icon="i-lucide-chevron-right"
            color="neutral"
            variant="ghost"
            size="xl"
            class="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full bg-black/40 text-white hover:bg-white/20 sm:right-4"
            aria-label="Next image"
            data-testid="image-lightbox-next"
            @click.stop="goTo(1)"
          />
        </template>

        <!-- Bottom bar: caption on the left, actions on the right. -->
        <div
          class="pointer-events-none fixed inset-x-0 bottom-0 z-10 flex items-end justify-between gap-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-[max(1rem,env(safe-area-inset-left))] pt-12 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <p
            v-if="caption"
            class="pointer-events-auto line-clamp-2 min-w-0 text-sm font-medium break-words text-white"
            data-testid="image-lightbox-caption"
            @click.stop
          >
            {{ caption }}
          </p>
          <span v-else />

          <div
            class="pointer-events-auto flex shrink-0 items-center gap-1 rounded-full bg-black/40 p-1"
            @click.stop
          >
            <UButton
              icon="i-lucide-zoom-out"
              color="neutral"
              variant="ghost"
              size="lg"
              :disabled="scale <= MIN_SCALE"
              class="text-white hover:bg-white/15 disabled:opacity-40"
              aria-label="Zoom out"
              data-testid="image-lightbox-zoom-out"
              @click="zoomOut"
            />
            <button
              type="button"
              class="min-w-14 rounded-full px-2 py-1 text-xs font-medium text-white tabular-nums transition-colors hover:bg-white/15"
              title="Reset zoom"
              data-testid="image-lightbox-zoom-reset"
              @click="resetView"
            >
              {{ Math.round(scale * 100) }}%
            </button>
            <UButton
              icon="i-lucide-zoom-in"
              color="neutral"
              variant="ghost"
              size="lg"
              :disabled="scale >= MAX_SCALE"
              class="text-white hover:bg-white/15 disabled:opacity-40"
              aria-label="Zoom in"
              data-testid="image-lightbox-zoom-in"
              @click="zoomIn"
            />
            <UButton
              v-if="downloadHref"
              icon="i-lucide-download"
              color="neutral"
              variant="ghost"
              size="lg"
              class="text-white hover:bg-white/15"
              aria-label="Download"
              data-testid="image-lightbox-download"
              @click="onDownload"
            />
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>
